/**
 * Selectable UI themes.
 *
 * The Tailwind palette (black, lochmara, keppel, ...) does not resolve to fixed
 * hex values but to CSS variables (see `tailwind.config.ts`). Every theme
 * defines those variables, so all existing utility classes (`bg-white`,
 * `text-black-900`, `text-lochmara-600`, ...) follow the active theme without
 * touching the components.
 *
 * Each color resolves differently depending on its ROLE, because the same shade
 * needs a different value as a background than as text in a dark theme:
 * - `surface`: backgrounds, gradients, shadows (`bg-*`, the default)
 * - `content`: text, icons and SVG paint (`text-*`, `fill-*`, `stroke-*`, ...)
 * - `line`: borders, dividers, rings and outlines (`border-*`, `ring-*`, ...)
 *
 * This file is imported by `tailwind.config.ts` (evaluated in Node), so it must
 * stay free of path aliases, Vue and browser APIs.
 */

const UI_THEMES = ['light', 'dark', 'high-contrast', 'high-contrast-dark'] as const
type UiTheme = (typeof UI_THEMES)[number]

/** A theme or `system`, which follows the OS `prefers-color-scheme`. */
const UI_THEME_PREFERENCES = ['system', ...UI_THEMES] as const
type UiThemePreference = (typeof UI_THEME_PREFERENCES)[number]

const DEFAULT_UI_THEME_PREFERENCE: UiThemePreference = 'system'

/** localStorage cache of the preference, read by the anti-flash script in index.html. */
const UI_THEME_STORAGE_KEY = 'ui-theme'

/**
 * Select options of the User field `tw_ui_theme` (Frappe stores the option label).
 * "Light" / "Dark" are avoided on purpose: the global de translation of "Light"
 * is "Leicht" (print designer font weight).
 */
const FRAPPE_UI_THEME_OPTIONS: Record<UiThemePreference, string> = {
  dark: 'Dark Mode',
  'high-contrast': 'High Contrast',
  'high-contrast-dark': 'High Contrast Dark',
  light: 'Light Mode',
  system: 'System',
}

const isUiThemePreference = (value: unknown): value is UiThemePreference => {
  return (
    typeof value === 'string' && UI_THEME_PREFERENCES.some((preference) => preference === value)
  )
}

const preferenceFromFrappeValue = (value: unknown): UiThemePreference | undefined => {
  const match = Object.entries(FRAPPE_UI_THEME_OPTIONS).find(([, label]) => label === value)
  return match && isUiThemePreference(match[0]) ? match[0] : undefined
}

// --- Palette ---------------------------------------------------------------

const SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const
type Shade = (typeof SHADES)[number]
type ColorScale = Record<Shade, string>

/** Neutral scale incl. `white`, which acts as the card / input surface. */
type NeutralScale = ColorScale & { white: string }

const NEUTRAL_LIGHT: NeutralScale = {
  50: '#F6F6F6',
  100: '#E7E7E7',
  200: '#D1D1D1',
  300: '#B0B0B0',
  400: '#888888',
  500: '#6D6D6D',
  600: '#5D5D5D',
  700: '#4F4F4F',
  800: '#454545',
  900: '#3D3D3D',
  950: '#000000',
  white: '#FFFFFF',
}

/** Brand / status scales. Themes remap their shades instead of redefining them. */
const ACCENT_PALETTE = {
  broom: {
    50: '#FDFEE8',
    100: '#FBFFC2',
    200: '#FBFF87',
    300: '#FFFD43',
    400: '#FFF229',
    500: '#EFD703',
    600: '#CEA900',
    700: '#A47904',
    800: '#885F0B',
    900: '#734D10',
    950: '#432905',
  },
  keppel: {
    50: '#F0FDFC',
    100: '#CCFBF6',
    200: '#99F6EC',
    300: '#5EEADB',
    400: '#2DD4C2',
    500: '#14B8A6',
    600: '#0D9485',
    700: '#0F766B',
    800: '#115E56',
    900: '#134E48',
    950: '#042F2A',
  },
  lightning_yellow: {
    50: '#FFFBEB',
    100: '#FFF3C6',
    200: '#FFE488',
    300: '#FFD14A',
    400: '#FFC133',
    500: '#F99907',
    600: '#DD7202',
    700: '#B74F06',
    800: '#943C0C',
    900: '#7A310D',
    950: '#461802',
  },
  lochmara: {
    50: '#F0F8FF',
    100: '#E0EFFE',
    200: '#B9DFFE',
    300: '#7CC7FD',
    400: '#36ACFA',
    500: '#0C91EB',
    600: '#007DDD',
    700: '#015AA3',
    800: '#064D86',
    900: '#0B416F',
    950: '#07294A',
  },
  lynch: {
    50: '#F6F7F9',
    100: '#ECEEF2',
    200: '#D5D9E2',
    300: '#B1BBC8',
    400: '#8695AA',
    500: '#64748B',
    600: '#526077',
    700: '#434E61',
    800: '#3A4252',
    900: '#343A46',
    950: '#23272E',
  },
  radical_red: {
    50: '#FFF1F3',
    100: '#FFE4E9',
    200: '#FECDD5',
    300: '#FDA4B3',
    400: '#FB7189',
    500: '#F43F5E',
    600: '#E11D3F',
    700: '#BE122F',
    800: '#9F122A',
    900: '#881327',
    950: '#4C0511',
  },
  seagull: {
    50: '#F1FAFE',
    100: '#E2F4FC',
    200: '#BFE9F8',
    300: '#84D8F2',
    400: '#47C5E9',
    500: '#1FAFD8',
    600: '#118DB8',
    700: '#0F7195',
    800: '#115F7B',
    900: '#134F67',
    950: '#0D3344',
  },
} satisfies Record<string, ColorScale>

const ACCENT_NAMES = Object.keys(ACCENT_PALETTE)

// --- Themes ----------------------------------------------------------------

const COLOR_ROLES = ['surface', 'content', 'line'] as const
type ColorRole = (typeof COLOR_ROLES)[number]

/** Which base shade a requested shade renders as, e.g. `{ 50: '950' }`. */
type ShadeMap = Record<Shade, Shade>

interface UiThemeDefinition {
  /** Accent shade remapping per role. */
  accents: Record<ColorRole, ShadeMap>
  /**
   * App / page background (`bg-canvas`). Separate from `black-50`, which is the subtle fill
   * INSIDE cards (bar tracks, table heads), in dark themes the two must differ: canvas darker
   * than cards, fills lighter.
   */
  canvas: string
  colorScheme: 'dark' | 'light'
  isHighContrast: boolean
  /** Neutral values per role. */
  neutrals: Record<ColorRole, NeutralScale>
}

const IDENTITY_SHADES: ShadeMap = {
  50: '50',
  100: '100',
  200: '200',
  300: '300',
  400: '400',
  500: '500',
  600: '600',
  700: '700',
  800: '800',
  900: '900',
  950: '950',
}

const shadeMap = (overrides: Partial<ShadeMap>): ShadeMap => ({ ...IDENTITY_SHADES, ...overrides })

// Light tints (50-300) become dark tints in every dark theme.
const DARK_TINTS: Partial<ShadeMap> = { 50: '950', 100: '900', 200: '800', 300: '700' }

const UI_THEME_DEFINITIONS: Record<UiTheme, UiThemeDefinition> = {
  dark: {
    accents: {
      // Solid fills (buttons, badges) keep their brand shade for white text.
      content: shadeMap({
        ...DARK_TINTS,
        400: '300',
        500: '400',
        600: '400',
        700: '300',
        800: '200',
        900: '100',
        950: '50',
      }),
      line: shadeMap({ ...DARK_TINTS, 50: '900' }),
      surface: shadeMap(DARK_TINTS),
    },
    canvas: '#0C0C0E',
    colorScheme: 'dark',
    isHighContrast: false,
    neutrals: {
      content: {
        50: '#1E1E22',
        100: '#2B2B31',
        200: '#3F3F46',
        300: '#71717A',
        400: '#8E8E96',
        500: '#A1A1AA',
        600: '#B4B4BC',
        700: '#C8C8CF',
        800: '#DCDCE1',
        900: '#EDEDF0',
        950: '#FAFAFA',
        white: '#FFFFFF',
      },
      line: {
        50: '#2A2A30',
        100: '#34343B',
        200: '#44444C',
        300: '#56565F',
        400: '#71717A',
        500: '#8E8E96',
        600: '#A1A1AA',
        700: '#C8C8CF',
        800: '#DCDCE1',
        900: '#EDEDF0',
        950: '#FAFAFA',
        white: '#1E1E22',
      },
      surface: {
        50: '#26262B',
        100: '#2E2E35',
        200: '#3A3A42',
        300: '#4D4D56',
        400: '#63636D',
        500: '#7A7A83',
        600: '#9A9AA3',
        700: '#B8B8C0',
        800: '#D4D4DA',
        900: '#EDEDF0',
        // Only used for modal backdrops, stays black.
        950: '#000000',
        white: '#1E1E22',
      },
    },
  },
  'high-contrast': {
    accents: {
      content: shadeMap({ 400: '700', 500: '700', 600: '700', 700: '800', 800: '900', 900: '950' }),
      line: shadeMap({
        50: '600',
        100: '600',
        200: '600',
        300: '600',
        400: '700',
        500: '700',
        600: '800',
        700: '900',
        800: '900',
      }),
      surface: shadeMap({ 400: '600', 500: '700', 600: '700', 700: '800', 800: '900' }),
    },
    canvas: '#F3F3F3',
    colorScheme: 'light',
    isHighContrast: true,
    neutrals: {
      content: {
        50: '#F3F3F3',
        100: '#E4E4E4',
        200: '#595959',
        300: '#4A4A4A',
        400: '#3D3D3D',
        500: '#2E2E2E',
        600: '#1F1F1F',
        700: '#000000',
        800: '#000000',
        900: '#000000',
        950: '#000000',
        white: '#FFFFFF',
      },
      line: {
        50: '#6D6D6D',
        100: '#4F4F4F',
        200: '#3D3D3D',
        300: '#2E2E2E',
        400: '#000000',
        500: '#000000',
        600: '#000000',
        700: '#000000',
        800: '#000000',
        900: '#000000',
        950: '#000000',
        white: '#FFFFFF',
      },
      surface: {
        50: '#F3F3F3',
        100: '#E4E4E4',
        200: '#D0D0D0',
        300: '#6D6D6D',
        400: '#4F4F4F',
        500: '#3D3D3D',
        600: '#1A1A1A',
        700: '#1A1A1A',
        800: '#1A1A1A',
        900: '#1A1A1A',
        950: '#000000',
        white: '#FFFFFF',
      },
    },
  },
  'high-contrast-dark': {
    accents: {
      content: shadeMap({
        ...DARK_TINTS,
        400: '200',
        500: '300',
        600: '300',
        700: '200',
        800: '100',
        900: '50',
        950: '50',
      }),
      line: shadeMap({
        50: '400',
        100: '400',
        200: '400',
        300: '400',
        400: '300',
        500: '300',
        600: '300',
        700: '300',
        800: '300',
      }),
      // 700 keeps white button labels at >= 7:1.
      surface: shadeMap({ ...DARK_TINTS, 500: '700', 600: '700', 700: '800', 800: '900' }),
    },
    canvas: '#0D0D0D',
    colorScheme: 'dark',
    isHighContrast: true,
    neutrals: {
      content: {
        50: '#0D0D0D',
        100: '#1F1F1F',
        200: '#BDBDBD',
        300: '#CCCCCC',
        400: '#D9D9D9',
        500: '#E6E6E6',
        600: '#FFFFFF',
        700: '#FFFFFF',
        800: '#FFFFFF',
        900: '#FFFFFF',
        950: '#FFFFFF',
        white: '#FFFFFF',
      },
      line: {
        50: '#A3A3A3',
        100: '#BDBDBD',
        200: '#D4D4D4',
        300: '#E0E0E0',
        400: '#FFFFFF',
        500: '#FFFFFF',
        600: '#FFFFFF',
        700: '#FFFFFF',
        800: '#FFFFFF',
        900: '#FFFFFF',
        950: '#FFFFFF',
        white: '#000000',
      },
      surface: {
        50: '#1A1A1A',
        100: '#1F1F1F',
        200: '#2E2E2E',
        300: '#8A8A8A',
        400: '#B0B0B0',
        500: '#C8C8C8',
        600: '#E6E6E6',
        700: '#E6E6E6',
        800: '#E6E6E6',
        900: '#E6E6E6',
        950: '#000000',
        white: '#000000',
      },
    },
  },
  light: {
    accents: { content: IDENTITY_SHADES, line: IDENTITY_SHADES, surface: IDENTITY_SHADES },
    canvas: '#F6F6F6',
    colorScheme: 'light',
    isHighContrast: false,
    neutrals: { content: NEUTRAL_LIGHT, line: NEUTRAL_LIGHT, surface: NEUTRAL_LIGHT },
  },
}

// --- CSS variables ---------------------------------------------------------

const cssVariableName = (role: ColorRole, scale: string, shade: string): string => {
  return `--tw-${role}-${scale}-${shade}`
}

/** `#RRGGBB` -> `R G B`, the channel format Tailwind's `<alpha-value>` needs. */
const hexToRgbChannels = (hex: string): string => {
  return [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)).join(' ')
}

/** All CSS variables (name -> RGB channels) a theme defines on <html>. */
const buildUiThemeCssVariables = (theme: UiTheme): Record<string, string> => {
  const definition = UI_THEME_DEFINITIONS[theme]
  const variables: Record<string, string> = {}

  for (const role of COLOR_ROLES) {
    const neutrals = definition.neutrals[role]
    variables[cssVariableName(role, 'white', 'DEFAULT')] = hexToRgbChannels(neutrals.white)
    variables[cssVariableName(role, 'canvas', 'DEFAULT')] = hexToRgbChannels(definition.canvas)

    for (const shade of SHADES) {
      variables[cssVariableName(role, 'black', shade)] = hexToRgbChannels(neutrals[shade])

      const mappedShade = definition.accents[role][shade]
      for (const [accent, scale] of Object.entries(ACCENT_PALETTE)) {
        variables[cssVariableName(role, accent, shade)] = hexToRgbChannels(scale[mappedShade])
      }
    }
  }

  return variables
}

type TailwindColorScale = Record<string, string>

/** Tailwind `colors` object for one role, pointing every shade at its CSS variable. */
const buildTailwindRoleColors = (role: ColorRole): Record<string, string | TailwindColorScale> => {
  const toColor = (scale: string, shade: string): string =>
    `rgb(var(${cssVariableName(role, scale, shade)}) / <alpha-value>)`
  const scaleColors = (scale: string): TailwindColorScale =>
    Object.fromEntries(SHADES.map((shade) => [shade, toColor(scale, shade)]))

  return {
    black: scaleColors('black'),
    canvas: toColor('canvas', 'DEFAULT'),
    ...Object.fromEntries(ACCENT_NAMES.map((accent) => [accent, scaleColors(accent)])),
    current: 'currentColor',
    transparent: 'transparent',
    white: toColor('white', 'DEFAULT'),
  }
}

export {
  buildTailwindRoleColors,
  buildUiThemeCssVariables,
  type ColorRole,
  DEFAULT_UI_THEME_PREFERENCE,
  FRAPPE_UI_THEME_OPTIONS,
  isUiThemePreference,
  preferenceFromFrappeValue,
  UI_THEME_DEFINITIONS,
  UI_THEME_PREFERENCES,
  UI_THEME_STORAGE_KEY,
  UI_THEMES,
  type UiTheme,
  type UiThemePreference,
}
